import {
  createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState,
  type KeyboardEvent as ReactKeyboardEvent, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * The one place a Review item opens: a drawer from the right, over the list.
 *
 * Review used to split the page — a 22rem list beside the detail — so the list was
 * cramped whenever anything was open, and a card's form was squeezed into whatever was
 * left. Now the list owns the page and an item opens over it:
 *
 * - from 768px wide (and tall enough to be a tablet or a desktop), a drawer up to 40rem
 *   wide — 64rem for an import, which carries a table — over a dimmed list that does not
 *   move;
 * - below that, and on a phone held sideways, a full-screen sheet with "← Back".
 *
 * It IS modal, and says so: the app behind it is `inert` while it is open (it is portalled
 * to the body beside the app), focus is kept inside, the page does not scroll, and a click
 * outside closes it. Named by its visible title, Escape closes it, and closing returns
 * focus to the row that opened it — the caller owns that row, so it passes `onClose` and
 * restores focus itself.
 *
 * The body scrolls on its own; the action footer does not. Any detail rendered inside can
 * put its decisions in `<DrawerFooter>`, which lands in the drawer's sticky footer — so
 * Approve, Send and Reject are reachable however long the form is — and renders inline
 * when the same component is used outside a drawer (a card photographed from Capture).
 * `<DrawerActions>` lays those decisions out the same way for every item: on a phone the
 * primary action alone, full width, with the others in one compact row beneath (or behind
 * "More actions" when there are more than two); from 768px one row, primary first.
 *
 * Where the item came from is said once, in the header: a detail puts it in
 * `<DrawerSource>`, and nothing in the body repeats it.
 *
 * A detail with unsaved edits says so with `useDrawerDirty(true)`. Closing then — Close,
 * Back, Escape, a click outside, or any guarded navigation inside — first asks "Discard
 * your changes?". A detail whose decision succeeded calls `useDrawerDone()` with what
 * happened; the list closes the drawer, says it, and moves focus to the next row.
 */

const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900';

interface DrawerContextValue {
  /** Where `<DrawerFooter>` content goes. Null on the first render, before it mounts. */
  footer: HTMLElement | null;
  /** Where `<DrawerSource>` content goes, in the header. */
  source: HTMLElement | null;
  /** One detail's unsaved-edits flag, by a key of its own. */
  setDirty: (key: string, dirty: boolean) => void;
  /** Runs `action` — after asking, when there are unsaved edits. */
  guard: (action: () => void) => void;
  /** A decision succeeded: close, say `message`, move on. Null when nobody listens. */
  done: ((message: string) => void) | null;
}
const DrawerContext = createContext<DrawerContextValue | null>(null);

/** Whether this component is rendered inside a Review drawer. */
export const useInDrawer = (): boolean => useContext(DrawerContext) !== null;

/** Marks the drawer as holding unsaved edits while `dirty` is true. A no-op outside one. */
export function useDrawerDirty(dirty: boolean): void {
  const ctx = useContext(DrawerContext);
  const key = useId();
  const setDirty = ctx?.setDirty;
  useEffect(() => {
    setDirty?.(key, dirty);
    return () => setDirty?.(key, false);
  }, [setDirty, key, dirty]);
}

/** Wraps an action that would lose unsaved edits (Back to the import) in the drawer's ask. */
export function useDrawerGuard(): (action: () => void) => void {
  const ctx = useContext(DrawerContext);
  return ctx?.guard ?? ((action) => action());
}

/** Reports a successful decision to the list. Null outside a drawer, or with no listener. */
export function useDrawerDone(): ((message: string) => void) | null {
  return useContext(DrawerContext)?.done ?? null;
}

/**
 * Where the item came from — "Captured from Gmail · Label: Leads" — said once, in the
 * drawer's header. Outside a drawer it renders where it is written.
 */
export function DrawerSource({ children }: { children: ReactNode }) {
  const ctx = useContext(DrawerContext);
  if (!ctx) return <div className="min-w-0">{children}</div>;
  return ctx.source ? createPortal(children, ctx.source) : null;
}

/** Each action's box: 44px tall, full width on a phone, its own width from 768px. */
const EACH = '[&>*]:min-h-11 [&>*]:w-full md:[&>*]:w-auto';
/** A secondary action that discards or rejects: still secondary, but not mistakable. */
export const DANGER = '!text-red-700 hover:!bg-red-50';

/**
 * A detail's decisions, laid out the same way for every item.
 *
 * Below 768px the primary action has a row of its own at full width — the one thing a
 * thumb is looking for — and the secondary actions share one compact row beneath it.
 * More than two secondaries fold behind "More actions". From 768px everything is one
 * row, primary first. Every action keeps a 44px target.
 */
export function DrawerActions({ primary, secondary = [], summary, children }: {
  /** The one action this step is for. */
  primary?: ReactNode;
  /** Everything else: buttons, in order of use. */
  secondary?: ReactNode[];
  /** A short lead, such as "3 selected", shown before the actions. */
  summary?: ReactNode;
  /** Notes under the actions: why one is disabled, what happened. */
  children?: ReactNode;
}) {
  const [more, setMore] = useState(false);
  const listId = useId();
  const shown = secondary.filter(Boolean);
  const fold = shown.length > 2;
  return (
    <div className="min-w-0 space-y-2">
      <div className="flex min-w-0 flex-col gap-2 md:flex-row md:flex-wrap md:items-center" data-drawer-actions="">
        {summary ? <div className="min-w-0 text-sm font-medium text-slate-800">{summary}</div> : null}
        {primary ? (
          <div className={`flex min-w-0 md:contents ${EACH}`} data-drawer-primary="">
            {primary}
          </div>
        ) : null}
        {shown.length > 0 && !fold ? (
          <div
            className={`grid min-w-0 gap-2 ${shown.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} md:contents ${EACH}`}
            data-drawer-secondary=""
          >
            {shown}
          </div>
        ) : null}
        {fold ? (
          <>
            <button
              type="button"
              aria-expanded={more}
              aria-controls={listId}
              onClick={() => setMore((v) => !v)}
              className={`inline-flex min-h-11 w-full items-center justify-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 md:hidden ${FOCUS}`}
            >
              {more ? 'Fewer actions' : 'More actions'}
            </button>
            <div id={listId} className={`${more ? 'grid' : 'hidden'} min-w-0 grid-cols-1 gap-2 md:contents ${EACH}`} data-drawer-secondary="">
              {shown}
            </div>
          </>
        ) : null}
      </div>
      {children}
    </div>
  );
}

/**
 * A detail's actions. Inside a drawer they go to its sticky footer; anywhere else they
 * render where they are written.
 */
export function DrawerFooter({ children, inlineClassName = 'pt-2' }: {
  children: ReactNode;
  /** Spacing when rendered in place, outside a drawer. */
  inlineClassName?: string;
}) {
  const ctx = useContext(DrawerContext);
  if (!ctx) return <div className={inlineClassName}>{children}</div>;
  return ctx.footer ? createPortal(children, ctx.footer) : null;
}

/**
 * A bordered card on a page; plain inside a drawer, which is already the surface. A card
 * inside a drawer inside a page is three frames around one form.
 */
export function DrawerSurface({ children, className = '', padded = 'p-5' }: {
  children: ReactNode; className?: string;
  /** The padding outside a drawer. Inside one, the drawer's own gutter is the padding. */
  padded?: string;
}) {
  const inDrawer = useInDrawer();
  return inDrawer
    ? <div className={className}>{children}</div>
    : <div className={`rounded-lg border border-slate-200 bg-white shadow-sm ${padded} ${className}`}>{children}</div>;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

export function ReviewDrawer({ title, subtitle, onClose, onDone, children, wide = false }: {
  /** The visible title the dialog is named by: the person, the company, or the source. */
  title: string;
  /** One quiet line under the title, when the title alone is ambiguous. */
  subtitle?: string | null;
  onClose: () => void;
  /** A decision inside succeeded. The list closes the drawer and moves on. */
  onDone?: (message: string) => void;
  children: ReactNode;
  /** An import: room for its table. */
  wide?: boolean;
}) {
  const titleId = useId();
  const confirmTitleId = useId();
  const confirmTextId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const confirmRef = useRef<HTMLDivElement | null>(null);
  const keepRef = useRef<HTMLButtonElement | null>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const [footer, setFooter] = useState<HTMLElement | null>(null);
  const [source, setSource] = useState<HTMLElement | null>(null);
  const [footerUsed, setFooterUsed] = useState(false);
  /** The action waiting on "Discard your changes?", when that is being asked. */
  const [pending, setPending] = useState<{ run: () => void } | null>(null);
  const dirty = useRef(new Map<string, boolean>());

  const setDirty = useCallback((key: string, value: boolean) => {
    if (value) dirty.current.set(key, true); else dirty.current.delete(key);
  }, []);
  const guard = useCallback((action: () => void) => {
    if (dirty.current.size === 0) { action(); return; }
    returnTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setPending({ run: action });
  }, []);
  const requestClose = useCallback(() => guard(onClose), [guard, onClose]);
  const done = useMemo(() => (onDone ? (message: string) => { dirty.current.clear(); onDone(message); } : null), [onDone]);
  const ctx = useMemo<DrawerContextValue>(
    () => ({ footer, source, setDirty, guard, done }),
    [footer, source, setDirty, guard, done],
  );

  const keepEditing = () => {
    setPending(null);
    const back = returnTo.current;
    requestAnimationFrame(() => (back && back.isConnected ? back : panelRef.current)?.focus());
  };
  const discard = () => {
    const run = pending?.run;
    dirty.current.clear();
    setPending(null);
    run?.();
  };

  // While the question is open, it is the only thing that can be reached.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (pending) { el.setAttribute('inert', ''); keepRef.current?.focus(); } else el.removeAttribute('inert');
  }, [pending]);

  /*
   * The page behind does not scroll while the drawer is open, and does not shift either:
   * hiding the scrollbar would widen the list by its width, so that width is given back
   * as padding for as long as the drawer is open.
   */
  useLayoutEffect(() => {
    const body = document.body;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    const before = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
    body.style.overflow = 'hidden';
    if (gap > 0) body.style.paddingRight = `${gap}px`;
    // And it is inert: the drawer is portalled beside the app, so the app itself can be.
    const app = document.getElementById('root');
    app?.setAttribute('inert', '');
    return () => {
      body.style.overflow = before.overflow; body.style.paddingRight = before.paddingRight;
      app?.removeAttribute('inert');
    };
  }, []);

  // Focus moves in on open. The caller returns it to the row on close.
  useEffect(() => { panelRef.current?.focus(); }, []);

  // The footer takes room only when something was put in it.
  useEffect(() => {
    if (!footer) return;
    const update = () => setFooterUsed(footer.childElementCount > 0);
    update();
    const mo = new MutationObserver(update);
    mo.observe(footer, { childList: true });
    return () => mo.disconnect();
  }, [footer]);

  /*
   * Escape closes; Tab cycles inside. On the panel rather than the document, so a control
   * inside that handles Escape itself (and prevents it) is not also closing the drawer.
   */
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && !e.defaultPrevented) {
      e.preventDefault();
      if (pending) keepEditing(); else requestClose();
      return;
    }
    const root = pending ? confirmRef.current : panelRef.current;
    if (e.key !== 'Tab' || !root) return;
    const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
      .filter((el) => el.offsetParent !== null || el === document.activeElement);
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) { e.preventDefault(); return; }
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panelRef.current || !root.contains(active))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && !root.contains(active)) { e.preventDefault(); first.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  };

  return createPortal(
    <DrawerContext.Provider value={ctx}>
      <div className="fixed inset-0 z-40" data-review-drawer="">
        {/* The backdrop: a click outside the drawer closes it. Not focusable; Escape is the keyboard way. */}
        <div
          aria-hidden="true"
          onClick={requestClose}
          className="absolute inset-0 hidden bg-slate-900/30 drawer:block motion-safe:animate-[signal-fade_150ms_ease-out]"
        />
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          onKeyDown={onKeyDown}
          data-review-detail=""
          className={`absolute inset-y-0 right-0 flex w-full min-w-0 flex-col bg-white shadow-xl outline-none drawer:border-l drawer:border-slate-200 ${
            wide ? 'drawer:w-[min(64rem,100%)]' : 'drawer:w-[min(40rem,100%)]'
          } motion-safe:animate-[signal-slide-in_200ms_ease-out]`}
        >
          <div ref={contentRef} className="flex min-h-0 min-w-0 flex-1 flex-col">
          <header className="flex min-w-0 shrink-0 items-start gap-2 border-b border-slate-200 px-4 py-1.5 sm:px-6">
            {/* A sheet that fills the screen gets a Back where a phone user looks for one. */}
            <button
              type="button"
              onClick={requestClose}
              className={`-ml-1 inline-flex min-h-11 shrink-0 items-center rounded px-1 text-sm text-slate-600 underline drawer:hidden ${FOCUS}`}
            >
              ← Back
            </button>
            <div className="min-w-0 flex-1 py-1.5">
              <h2 id={titleId} title={title} className="truncate text-base font-semibold text-slate-900">
                {title}
              </h2>
              {subtitle ? (
                <p className="truncate text-sm text-slate-600" title={subtitle}>{subtitle}</p>
              ) : null}
              {/* Where it came from: once, here. A detail fills it with <DrawerSource>. */}
              <div ref={setSource} className="min-w-0 text-xs text-slate-500 empty:hidden [&>*]:line-clamp-2" data-drawer-source="" />
            </div>
            <button
              type="button"
              onClick={requestClose}
              aria-label="Close"
              className={`-mr-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-2xl leading-none text-slate-500 hover:bg-slate-100 hover:text-slate-900 ${FOCUS}`}
            >
              <span aria-hidden="true">×</span>
            </button>
          </header>
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6" data-drawer-body="">
            {children}
          </div>
          <div
            ref={setFooter}
            data-drawer-footer=""
            className={`shrink-0 border-t border-slate-200 bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6 ${footerUsed ? '' : 'hidden'}`}
          />
          </div>
          {pending ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-900/40 p-4" data-discard-confirm="">
              <div
                ref={confirmRef}
                role="alertdialog"
                aria-labelledby={confirmTitleId}
                aria-describedby={confirmTextId}
                className="w-full max-w-sm min-w-0 rounded-lg bg-white p-5 shadow-xl"
              >
                <h3 id={confirmTitleId} className="text-base font-semibold text-slate-900">Discard your changes?</h3>
                <p id={confirmTextId} className="mt-1 text-sm text-slate-600">
                  The changes you made here have not been saved.
                </p>
                <div className="mt-4 flex flex-col gap-2 sm:flex-row-reverse">
                  <button
                    ref={keepRef} type="button" onClick={keepEditing}
                    className={`inline-flex min-h-11 items-center justify-center rounded-md bg-slate-900 px-4 text-sm font-medium text-white hover:bg-slate-800 ${FOCUS}`}
                  >
                    Keep editing
                  </button>
                  <button
                    type="button" onClick={discard}
                    className={`inline-flex min-h-11 items-center justify-center rounded-md border border-slate-300 bg-white px-4 text-sm font-medium ${DANGER} ${FOCUS}`}
                  >
                    Discard
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </DrawerContext.Provider>,
    document.body,
  );
}
